import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from 'designSystem/components/Popover'
import { DOC_CHAT_FEEDBACK, useGeckoAnalytics } from 'features/analytics'
import { DocChatFeedbackDialog } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/DocChatFeedbackDialog'
import { SelectableAnswerRegion } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/docChatStyles'
import {
  type DocChatFeedbackTurn,
  useDocChatFeedbackBase,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatFeedbackBase'
import { useDocChatFeedbackDialog } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatFeedbackDialog'
import { useDocChatSelection } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatSelection'
import { useSubmitDocChatFeedback } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useSubmitDocChatFeedback'
import type { DocChatFeedbackReason } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatFeedback'
import {
  DOCCHAT_SELECTION_BAR_ATTRIBUTE,
  type DocChatSelectionRect,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatSelection'
import { MessageSquareText } from 'lucide-react'
import React, { useCallback, useMemo, useRef, useState } from 'react'

export type DocChatSelectionFeedbackProps = DocChatFeedbackTurn & {
  children: React.ReactNode
  enabled: boolean
}

/**
 * Lets the user select part of an answer and give feedback on that part.
 * Selecting text shows a "Give feedback" popover anchored to the selection;
 * the dialog then quotes the selection.
 */
export function DocChatSelectionFeedback({
  children,
  enabled,
  ...turn
}: DocChatSelectionFeedbackProps): React.ReactElement {
  const { t } = useLingui()
  const { trackEvent } = useGeckoAnalytics()
  const submitFeedback = useSubmitDocChatFeedback()
  const { canGiveFeedback, feedback } = useDocChatFeedbackBase(turn)
  const dialog = useDocChatFeedbackDialog()
  const { open: openFeedbackDialog, submit } = dialog
  const containerRef = useRef<HTMLDivElement>(null)
  const [selection, clearSelection] = useDocChatSelection(containerRef, enabled)
  const [quote, setQuote] = useState<string | null>(null)

  const anchorRef = useMemo(
    () => ({ current: selection ? selectionAnchor(selection.rect) : null }),
    [selection]
  )

  const openDialog = useCallback(() => {
    if (!selection) return
    setQuote(selection.text)
    openFeedbackDialog()
    clearSelection()
  }, [clearSelection, openFeedbackDialog, selection])

  const handleSubmit = useCallback(
    (reasons: DocChatFeedbackReason[], comment: string) => {
      void submit(() =>
        submitFeedback({ ...feedback, reasons, comment, selectedText: quote })
      ).then((sent) => {
        if (!sent) return
        trackEvent(DOC_CHAT_FEEDBACK.title, {
          rating: 'down',
          site_id: feedback.siteId,
          search_query: turn.retryQuery,
          reasons,
          has_comment: comment.trim().length > 0,
          source: 'selection',
        })
      })
    },
    [feedback, quote, submit, submitFeedback, trackEvent, turn.retryQuery]
  )

  const showBar = canGiveFeedback && selection !== null && !dialog.isOpen
  const barAttribute = { [DOCCHAT_SELECTION_BAR_ATTRIBUTE]: '' }

  return (
    <>
      <SelectableAnswerRegion ref={containerRef}>
        {children}
      </SelectableAnswerRegion>
      <Popover
        open={showBar}
        onOpenChange={(open) => {
          if (!open) clearSelection()
        }}>
        <PopoverAnchor virtualRef={anchorRef} />
        <PopoverContent
          side='top'
          layout='flushHug'
          sideOffset={8}
          {...barAttribute}
          onOpenAutoFocus={(event) => event.preventDefault()}>
          <Button type='button' size='lg' variant='ghost' onClick={openDialog}>
            {t`Give feedback`}
            <MessageSquareText data-icon='inline-end' />
          </Button>
        </PopoverContent>
      </Popover>
      {dialog.isOpen ? (
        <DocChatFeedbackDialog
          open
          isSubmitting={dialog.isSubmitting}
          submitted={dialog.submitted}
          quote={quote}
          error={dialog.error}
          onOpenChange={dialog.onOpenChange}
          onSubmit={handleSubmit}
        />
      ) : null}
    </>
  )
}

/**
 * A zero-width point at the top middle of the selection. Radix anchors to
 * anything with `getBoundingClientRect`; a zero width keeps the popover
 * sized by its content instead of stretching to the selected text.
 */
function selectionAnchor(rect: DocChatSelectionRect) {
  return {
    getBoundingClientRect: () =>
      new DOMRect(rect.left + rect.width / 2, rect.top, 0, rect.height),
  }
}
